import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Typography } from "@mui/material";
import AddButton from "../../components/ui/AddButton";
import { useNotification } from "../../context/NotificationContext";
import Breadcrumb from "../../components/common/Breadcrumb";
import NavigationTags from "../../components/common/NavigationTags";
import { generateSingleTrackCreationBreadcrumbs } from "../../utils/breadcrumbHelpers";
import { GetSingleTrackResponse, CompanySingleTrackFolder } from "../../types/requestsOutputs";
import { getAllSingleTracks } from "../../api/singleTrack";
import { getSingleTrackFolders, getSingleTrackFirstFilters } from "../../api/company";
import { getCategories } from "../../api/categories";
import { generateQRCodeFromText } from "../../api/support";
import { SingleTrackPanel } from "../../components/cards/SingleTrack";
import UnifiedCreateSingleTrackModal from "../../components/modals/UnifiedCreateSingleTrackModal";
import hubStyles from "./Hub.module.css";
import pageHeader from "../../components/layouts/PageHeader.module.css";

const SingleTrackCreation: React.FC = () => {
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const categoryId = searchParams.get("categoryId");
    const hubId = searchParams.get("hubId");

    const [tracks, setTracks] = useState<GetSingleTrackResponse>([]);
    const [hub, setHub] = useState<CompanySingleTrackFolder | null>(null);
    const [loading, setLoading] = useState(true);
    const [folderName, setFolderName] = useState<string>("");
    const [categoryName, setCategoryName] = useState<string>("");
    const [createModalOpen, setCreateModalOpen] = useState(false);

    const { showNotification } = useNotification();

    // Fetch hub and tracks on component mount
    useEffect(() => {
        if (hubId) {
            fetchHub();
        }
        fetchTracks();
        fetchNames();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubId, categoryId, i18n.language]);

    const fetchNames = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            // Fetch hub name
            if (hubId) {
                const hubsResponse = await getSingleTrackFolders({
                    token,
                    data: { language: i18n.language || "en" },
                });
                const currentHub = hubsResponse.find((h) => h.id === hubId);
                if (currentHub) {
                    // Hub name will be set via the existing fetchHub function
                }
            }

            // Fetch folder name
            if (folderId && hubId) {
                const foldersResponse = await getSingleTrackFirstFilters({
                    token,
                    data: {
                        companySingleTrackFolderId: hubId,
                        language: i18n.language || "en",
                    },
                });
                const currentFolder = foldersResponse.find((f) => f.id === folderId);
                if (currentFolder) {
                    setFolderName(currentFolder.internalName);
                }
            }

            // Fetch category name
            if (categoryId && folderId) {
                const categoriesResponse = await getCategories({
                    token,
                    data: { firstFilterId: folderId },
                });
                const currentCategory = categoriesResponse.find((c) => c.id === categoryId);
                if (currentCategory) {
                    setCategoryName(currentCategory.name);
                }
            }
        } catch (error) {
            console.error("Error fetching names:", error);
        }
    };

    const fetchHub = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token || !hubId) {
                return;
            }

            const response = await getSingleTrackFolders({
                token,
                data: { language: i18n.language || "en" },
            });
            const currentHub = response.find((h) => h.id === hubId);
            if (currentHub) {
                setHub(currentHub);
            }
        } catch (error) {
            console.error("Error fetching hub:", error);
            showNotification({
                message: t("errorFetchingHub"),
                type: "error",
            });
        }
    };

    const fetchTracks = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getAllSingleTracks({
                token,
                data: {
                    folderId: hubId || undefined,
                    categoryId: categoryId || undefined,
                },
            });
            setTracks(response);
        } catch (error) {
            console.error("Failed to fetch tracks", error);
            showNotification({
                message: t("errorFetchingTracks"),
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleQRCodeDownload = async (OTPToken: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No token found");
            return;
        }

        try {
            const qrText = `hub.rewindstories.com/?id=${OTPToken}`;
            const response = await generateQRCodeFromText(token, qrText);

            window.open(response.url, "_blank");
        } catch (error) {
            console.error("Failed to generate QR code:", error);
        }
    };

    const handleCreateNewTrack = () => {
        setCreateModalOpen(true);
    };

    return (
        <div className={`${hubStyles.root} ${hubStyles.page}`}>
            <div className={hubStyles.pageHeader}>
                <Breadcrumb
                    classes={{
                        nav: hubStyles.breadcrumbNav,
                        separator: hubStyles.breadcrumbSeparator,
                        link: hubStyles.breadcrumbLink,
                        text: hubStyles.breadcrumbText,
                        active: hubStyles.breadcrumbActive,
                    }}
                    items={generateSingleTrackCreationBreadcrumbs({
                        hubId,
                        hubName: hub?.internalName || "",
                        folderId,
                        folderName,
                        categoryId,
                        categoryName,
                        t,
                    })}
                />
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className={hubStyles.settingsTitle}>{t("singleTracks")}</h2>
                        <Typography variant="body2" className={hubStyles.pageSubtitle}>
                            {t("manageSingleTracksDescription")}
                        </Typography>
                    </div>
                    <AddButton
                        labeledClassName={hubStyles.addButtonLabeled}
                        onClick={handleCreateNewTrack}
                        tooltip={t("createNewTrack")}
                        label={t("newContent")}
                    />
                </div>
                <NavigationTags
                    classes={{
                        container: hubStyles.navigationTags,
                        tag: hubStyles.navigationTag,
                        tagActive: hubStyles.navigationTagActive,
                        tagDisabled: hubStyles.navigationTagDisabled,
                        arrow: hubStyles.navigationTagArrow,
                    }}
                    tags={[
                        { label: t("hub"), path: "/hubs" },
                        { label: t("page"), path: `/hubs/folders?hubId=${hubId}` },
                        { label: t("category"), path: `/hubs/folders/categories?folderId=${folderId}&hubId=${hubId}` },
                        { label: t("content"), isActive: true },
                    ]}
                />
            </div>

            {/* Tracks List */}
            <div className="tracks-section">
                {loading ? (
                    <div className="form-loading-container">
                        <div className={hubStyles.formLoadingSpinner}></div>
                        <p className="form-loading-text">{t("loadingTracks")}</p>
                    </div>
                ) : tracks.length === 0 ? (
                    <div className={hubStyles.emptyState}>
                        <div className={hubStyles.emptyStateIcon} aria-hidden>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M9 18V5l12-2v13" />
                                <circle cx="6" cy="18" r="3" />
                                <circle cx="18" cy="16" r="3" />
                            </svg>
                        </div>
                        <Typography variant="h6" className={hubStyles.emptyStateTitle}>
                            {t("noTracksYet")}
                        </Typography>
                        <Typography variant="body2" className={hubStyles.emptyStateText}>
                            {t("noTracksDescription")}
                        </Typography>
                        <AddButton
                            labeledClassName={hubStyles.emptyStateButton}
                            onClick={handleCreateNewTrack}
                            tooltip={t("createYourFirstTrack")}
                            label={t("createYourFirstTrack")}
                        />
                    </div>
                ) : (
                    <SingleTrackPanel
                        singleTracks={tracks}
                        locale="iduniquefr"
                        refetch={fetchTracks}
                        onQRCodeDownload={handleQRCodeDownload}
                        hub={hub}
                        folderId={folderId}
                        categoryId={categoryId}
                    />
                )}
            </div>

            {/* Create content modal */}
            <UnifiedCreateSingleTrackModal
                isOpen={createModalOpen}
                onClose={() => setCreateModalOpen(false)}
                onSuccess={fetchTracks}
                hubId={hubId}
                folderId={folderId}
                categoryId={categoryId}
                hub={hub}
            />
        </div>
    );
};

export default SingleTrackCreation;
